import { ProFormColumnsType } from '@ant-design/pro-components';
import { Card } from 'antd';
import { IncomeType } from '../../../../api/btlApplicationForm/types';
import { DeleteIcon } from '@components/common/Icons/DeleteIcon';
import { BaseFormSchemaProps } from '../../types';
import { createCurrencyFormItem } from './createCurrencyFormItem';
import {
  AddEmploymentIncomeButton,
  IncomeListItemIndexContext,
  MemberEmploymentTypeSelect,
} from './householdMemberEmploymentFields';
import { companyDirectorFormSchema } from '@pages/BtlApplicationFormPage/TopSlicing/Household/MemberIncome/companyDirector';
import { contractorFormSchema } from '@pages/BtlApplicationFormPage/TopSlicing/Household/MemberIncome/contractor';
import { partnershipFormSchema } from '@pages/BtlApplicationFormPage/TopSlicing/Household/MemberIncome/partnership';
import { payeEmployeeFormSchema } from '@pages/BtlApplicationFormPage/TopSlicing/Household/MemberIncome/payeEmployee';
import { soleTraderFormSchema } from '@pages/BtlApplicationFormPage/TopSlicing/Household/MemberIncome/soleTrader';
export type HouseholdMemberSchemaProps = BaseFormSchemaProps & {
  isEmploymentIncomeRequired?: boolean;
  isPrimaryApplicant?: boolean;
  variant?: 'btl' | 'oo';
};

export const householdMemberFormSchema = (props: HouseholdMemberSchemaProps): ProFormColumnsType[] => [
  {
    dataIndex: ['incomes'],
    valueType: 'formList',
    fieldProps: {
      copyIconProps: false,
      max: 5,
      creatorButtonProps: false,
      itemRender: ({ listDom }: any, { index, operation }: any) => (
        <IncomeListItemIndexContext.Provider value={index}>
          <Card
            title={
              index === 0
                ? props.formatMessage({
                    id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.primaryIncome.label',
                  })
                : props.formatMessage({
                    id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.secondaryIncome.label',
                  })
            }
            className={'bg-white/60 border-gray-300 border-dashed'}
            extra={
              operation.remove && props.isFormEditable ? (
                <a onClick={() => operation.remove(index)}>
                  <DeleteIcon
                    titleAccess={props.formatMessage({
                      id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.removeEmploymentIncome.button',
                    })}
                  />
                </a>
              ) : null
            }
            style={{ marginBottom: 16 }}
          >
            {listDom}
          </Card>
        </IncomeListItemIndexContext.Provider>
      ),
    },
    columns: [
      {
        dataIndex: 'id',
        valueType: 'text',
        formItemProps: { hidden: true, noStyle: true },
        fieldProps: { hidden: true },
      },
      {
        title: props.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.label' }),
        dataIndex: ['employmentType'],
        valueType: 'select',
        renderFormItem: (_schema, config) => (
          <MemberEmploymentTypeSelect
            value={config.value}
            isPrimaryApplicant={Boolean(props.isPrimaryApplicant)}
            formatMessage={props.formatMessage}
          />
        ),
        formItemProps: {
          hasFeedback: true,
          rules: [
            {
              message: props.formatMessage({
                id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.employmentType.error',
              }),
              required: true,
            },
          ],
        },
      },
      {
        valueType: 'dependency',
        name: ['employmentType'],
        columns: ({ employmentType }: { employmentType: IncomeType }) => {
          const includePercentageShareholding = props.variant === 'oo';

          switch (employmentType) {
            case IncomeType.PAYE_EMPLOYEE:
              return [...payeEmployeeFormSchema()];
            case IncomeType.SOLE_TRADER:
              return [...soleTraderFormSchema()];
            case IncomeType.CONTRACTOR:
              return [...contractorFormSchema()];
            case IncomeType.SALARIED_DIRECTOR:
              return [...companyDirectorFormSchema({ includePercentageShareholding })];
            case IncomeType.PARTNERSHIP:
              return [...partnershipFormSchema({ includePercentageShareholding })];
            case IncomeType.UNEMPLOYED:
            case IncomeType.RETIRED:
            default:
              return [];
          }
        },
      },
    ],
  },
  {
    renderFormItem: () => (
      <AddEmploymentIncomeButton isFormEditable={props.isFormEditable} formatMessage={props.formatMessage} />
    ),
  },
  {
    valueType: 'group',
    columns: [
      {
        renderFormItem: () => (
          <h4 className={'mb-0'}>
            {props.formatMessage({ id: 'btlApplicationForm.TOP_SLICING_HOUSEHOLD.member.otherIncome.header' })}
          </h4>
        ),
      },
      ...['pension', 'rental', 'maintenanceReceipts'].map(item =>
        createCurrencyFormItem('otherIncome', item, 'member', 'yearly'),
      ),
    ],
  },
];
